/**
 * nativePush — FCM push registration + foreground presentation for the
 * Capacitor Android shell.
 *
 * Architecture (mirrors the existing web-push design so the server needs no
 * new tables):
 * - On login (native only) we request POST_NOTIFICATIONS, register with FCM
 *   via @capacitor/push-notifications, and store the device token as a row in
 *   `public.push_tokens` — one row per device, so a user with more than one
 *   phone/tablet receives on all of them. (Until 2026-08-02 this was a single
 *   `user_preferences.behavior_data.fcm_token` field that each new
 *   registration overwrote, so only the last device ever got a push.)
 * - The existing `notifications` INSERT trigger → `send-push-notification`
 *   edge function fans out to BOTH transports: web-push (unchanged) and FCM
 *   v1 (new, needs the FCM_SERVICE_ACCOUNT secret server-side).
 * - Backgrounded/closed app: FCM "notification" messages are rendered by the
 *   OS with sound + vibration on the `behtari_default` channel.
 * - Foregrounded app: Android does NOT display FCM notification messages, so
 *   `pushNotificationReceived` re-presents them via @capacitor/local-notifications
 *   (same channel → same sound/vibration).
 *
 * Web/PWA is untouched — every entry point is isNativeApp()-gated and the
 * web path in usePushSubscription keeps using webPushService.
 */
import { PushNotifications, type PushNotificationSchema, type ActionPerformed } from '@capacitor/push-notifications';
import { LocalNotifications } from '@capacitor/local-notifications';
import { isNativeApp } from '@/lib/nativeApp';
import { supabase } from '@/integrations/supabase/client';
import { captureError } from '@/utils/captureError';
import type { Json } from '@/integrations/supabase/types';

const CHANNEL_ID = 'behtari_default';

let initialized = false;

/** Android 8+ notification channel — sound + vibration for both FCM (background)
 *  and LocalNotifications (foreground re-presentation). Idempotent. */
async function ensureChannel(): Promise<void> {
  try {
    // No `sound` field: omitting it selects the system default notification
    // sound — passing a name would try to load a res/raw resource instead.
    await PushNotifications.createChannel({
      id: CHANNEL_ID,
      name: 'Behtari',
      description: 'Calls, messages and job updates',
      importance: 5,
      visibility: 1,
      vibration: true,
    });
  } catch (e) {
    // createChannel is Android-only and can throw on older plugin/OS combos —
    // FCM then falls back to the default channel, which still notifies.
    captureError('native-push', e, { stage: 'create-channel' });
  }
}

/** localStorage key holding THIS device's FCM token, so logout can delete
 *  exactly this device's push_tokens row and leave the user's other devices
 *  registered. */
const LOCAL_TOKEN_KEY = 'behtari_fcm_token';

async function saveTokenToServer(token: string): Promise<void> {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) return;

  try { localStorage.setItem(LOCAL_TOKEN_KEY, token); } catch { /* non-fatal */ }

  // Multi-device (C1, 2026-08-02): registrations live in public.push_tokens,
  // one row per device, so a second phone/tablet no longer silently
  // unregisters the first (claim_fcm_token's ON CONFLICT (user_id) DO UPDATE
  // overwrote the single behavior_data field).
  //
  // Exclusive claim is preserved: push_tokens has UNIQUE (token) and the RPC's
  // ON CONFLICT (token) DO UPDATE reassigns user_id, so a shared handset still
  // belongs to exactly one account and the cross-user notification leak stays
  // closed. Do NOT revert to a client-side read-modify-write here.
  // `as any` until the Lovable agent regenerates types.ts with this RPC.
  const { error } = await supabase.rpc('claim_push_token' as any, {
    _token: token,
    _platform: 'android',
  });
  if (error) captureError('native-push', error, { stage: 'claim-token' });
}

/** Foreground FCM arrival — Android suppresses the system notification, so we
 *  re-present it as an immediate local notification (sound + vibration). */
async function presentForeground(notification: PushNotificationSchema): Promise<void> {
  try {
    const title = notification.title || notification.data?.title || 'Behtari';
    const body = notification.body || notification.data?.body || '';
    await LocalNotifications.schedule({
      notifications: [{
        id: Math.floor(Date.now() % 2147483647),
        title,
        body,
        channelId: CHANNEL_ID,
        extra: notification.data ?? {},
        smallIcon: 'ic_launcher',
      }],
    });
  } catch (e) {
    captureError('native-push', e, { stage: 'foreground-present' });
  }
}

function navigateFromData(data: Record<string, unknown> | undefined): void {
  const url = typeof data?.url === 'string' ? data.url : undefined;
  if (!url || !url.startsWith('/')) return;
  // SPA navigation without a hard reload — App.tsx listens for this event and
  // routes via react-router (navigateRef), so contexts/session stay mounted.
  window.dispatchEvent(new CustomEvent('behtari:push-navigate', { detail: { url } }));
}

/**
 * Initialize native push for the logged-in user. Safe to call multiple times;
 * listeners register once per app lifetime. Returns whether registration was
 * kicked off (false = not native / permission denied / not supported).
 */
export async function initNativePush(): Promise<boolean> {
  if (!isNativeApp()) return false;

  try {
    let permStatus = await PushNotifications.checkPermissions();
    if (permStatus.receive === 'prompt' || permStatus.receive === 'prompt-with-rationale') {
      permStatus = await PushNotifications.requestPermissions();
    }
    if (permStatus.receive !== 'granted') {
      // User declined POST_NOTIFICATIONS — respect it, no retry nagging.
      return false;
    }

    await ensureChannel();

    if (!initialized) {
      initialized = true;

      await PushNotifications.addListener('registration', (token) => {
        saveTokenToServer(token.value).catch((e) =>
          captureError('native-push', e, { stage: 'save-token' }));
      });

      await PushNotifications.addListener('registrationError', (err) => {
        captureError('native-push', new Error(err.error), { stage: 'registration' });
      });

      await PushNotifications.addListener('pushNotificationReceived', (notification) => {
        void presentForeground(notification);
      });

      await PushNotifications.addListener('pushNotificationActionPerformed', (action: ActionPerformed) => {
        navigateFromData(action.notification.data as Record<string, unknown> | undefined);
      });

      await LocalNotifications.addListener('localNotificationActionPerformed', (action) => {
        navigateFromData(action.notification.extra as Record<string, unknown> | undefined);
      });
    }

    // register() is idempotent; re-registering refreshes the token if FCM
    // rotated it while we were away.
    await PushNotifications.register();
    return true;
  } catch (e) {
    captureError('native-push', e, { stage: 'init' });
    return false;
  }
}

/** Remove THIS device's push registration (logout / notifications disabled).
 *
 *  Deletes only this device's push_tokens row — the user's other devices stay
 *  registered, which is the whole point of the multi-device model. The legacy
 *  behavior_data.fcm_token field is cleared too, so a device that registered
 *  before the 2026-08-02 migration doesn't leave a stale entry behind for the
 *  edge function's fallback read to pick up. */
export async function clearNativePushToken(): Promise<void> {
  if (!isNativeApp()) return;
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return;

    let localToken: string | null = null;
    try { localToken = localStorage.getItem(LOCAL_TOKEN_KEY); } catch { /* ignore */ }

    if (localToken) {
      // RLS: "Users can delete their own push tokens" (user_id = auth.uid()).
      await supabase.from('push_tokens' as never).delete().eq('token', localToken);
      try { localStorage.removeItem(LOCAL_TOKEN_KEY); } catch { /* ignore */ }
    }

    const { data: currentPrefs } = await supabase
      .from('user_preferences')
      .select('behavior_data')
      .eq('user_id', user.id)
      .maybeSingle();
    const behaviorData = (currentPrefs?.behavior_data as Record<string, unknown>) || {};
    if (!('fcm_token' in behaviorData)) return;
    const { fcm_token: _drop, ...rest } = behaviorData;
    await supabase
      .from('user_preferences')
      .update({ behavior_data: rest as Json })
      .eq('user_id', user.id);
  } catch (e) {
    captureError('native-push', e, { stage: 'clear-token' });
  }
}
